<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from './Button.vue';

interface Series {
  label: string;
  values: number[];
  color: string;
}
const props = defineProps<{
  kind: 'columns' | 'donut';
  title: string;
  labels: string[];
  series: Series[];
  totalLabel: string;
  dataLabel: string;
  locale: string;
}>();
function safeValue(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}
function seriesSum(seriesItem: Series): number {
  return seriesItem.values.reduce((sum, value) => sum + safeValue(value), 0);
}
const showData = ref(false);
const total = computed(() => props.series.reduce((sum, series) => sum + seriesSum(series), 0));
const maximum = computed(() =>
  Math.max(1, ...props.series.flatMap((series) => series.values.map((v) => safeValue(v)))),
);
const axisMax = computed(() => Math.ceil(maximum.value / 4) * 4);
const slotWidth = computed(() => 620 / Math.max(1, props.labels.length));
const barWidth = computed(() => Math.min(20, (slotWidth.value - 12) / Math.max(1, props.series.length)));
const donut = computed(() => {
  let offset = 0;
  return props.series.map((series) => {
    const value = seriesSum(series);
    const fraction = total.value ? value / total.value : 0;
    const segment = { ...series, value, length: fraction * 100, offset };
    offset += fraction * 100;
    return segment;
  });
});
function number(value: number) {
  return new Intl.NumberFormat(props.locale).format(Number.isFinite(value) ? value : 0);
}
</script>

<template>
  <figure class="m-0 min-w-0">
    <div v-if="kind === 'columns'" class="overflow-x-auto" dir="ltr">
      <svg class="block w-full min-w-[32rem]" viewBox="0 0 700 250" role="img" :aria-label="title">
        <title>{{ title }}</title>
        <g v-for="tick in [0, 1, 2, 3, 4]" :key="tick">
          <line x1="48" x2="682" :y1="210 - tick * 45" :y2="210 - tick * 45" class="chart-grid" />
          <text x="38" :y="214 - tick * 45" text-anchor="end" class="chart-label">
            {{ number((axisMax * tick) / 4) }}
          </text>
        </g>
        <g v-for="(label, index) in labels" :key="index">
          <g v-for="(seriesItem, seriesIndex) in series" :key="seriesItem.label">
            <rect
              :x="54 + index * slotWidth + (slotWidth - series.length * barWidth) / 2 + seriesIndex * barWidth"
              :y="210 - (safeValue(seriesItem.values[index]) / axisMax) * 180"
              :width="Math.max(1, barWidth - 3)"
              :height="(safeValue(seriesItem.values[index]) / axisMax) * 180"
              rx="3"
              :fill="seriesItem.color"
            >
              <title>{{ label }} · {{ seriesItem.label }}: {{ number(safeValue(seriesItem.values[index])) }}</title>
            </rect>
          </g>
          <text :x="54 + index * slotWidth + slotWidth / 2" y="236" text-anchor="middle" class="chart-label">
            {{ label }}
          </text>
        </g>
      </svg>
    </div>
    <div v-else class="relative mx-auto size-52">
      <svg class="size-full -rotate-90" viewBox="0 0 220 220" role="img" :aria-label="title">
        <title>{{ title }}</title>
        <circle cx="110" cy="110" r="83" fill="none" class="chart-track" stroke-width="22" />
        <circle
          v-for="segment in donut"
          :key="segment.label"
          cx="110"
          cy="110"
          r="83"
          fill="none"
          stroke-width="22"
          pathLength="100"
          :stroke="segment.color"
          :stroke-dasharray="`${segment.length} ${100 - segment.length}`"
          :stroke-dashoffset="-segment.offset"
        >
          <title>{{ segment.label }}: {{ number(segment.value) }}</title>
        </circle>
      </svg>
      <div class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        <span class="text-3xl font-semibold tabular-nums text-jv-on-surface">{{ number(total) }}</span>
        <span class="mt-1 text-xs text-jv-on-surface-variant">{{ totalLabel }}</span>
      </div>
    </div>
    <figcaption class="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-jv-on-surface-variant">
      <span v-for="seriesItem in series" :key="seriesItem.label" class="inline-flex items-center gap-2">
        <span class="size-2.5 shrink-0 rounded-sm" :style="{ background: seriesItem.color }" aria-hidden="true" />
        {{ seriesItem.label }}
        <strong class="tabular-nums text-jv-on-surface">{{ number(seriesSum(seriesItem)) }}</strong>
      </span>
    </figcaption>
    <div class="mt-3 text-center">
      <Button text size="small" :label="dataLabel" :aria-expanded="showData" @click="showData = !showData" />
    </div>
    <div v-if="showData" class="mt-3 overflow-x-auto">
      <table class="w-full text-start text-xs text-jv-on-surface">
        <caption class="sr-only">
          {{
            title
          }}
        </caption>
        <thead>
          <tr>
            <th scope="col" class="p-2 text-start">{{ totalLabel }}</th>
            <th v-for="seriesItem in series" :key="seriesItem.label" scope="col" class="p-2 text-start">
              {{ seriesItem.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(label, index) in labels" :key="index" class="border-t border-jv-outline-variant/20">
            <th scope="row" class="p-2 text-start">{{ label }}</th>
            <td v-for="seriesItem in series" :key="seriesItem.label" class="p-2 tabular-nums">
              {{ number(safeValue(seriesItem.values[index])) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </figure>
</template>

<style scoped>
.chart-grid {
  stroke: var(--color-jv-outline-variant);
  stroke-opacity: 0.25;
}
.chart-label {
  fill: var(--color-jv-on-surface-variant);
  font-size: 11px;
}
.chart-track {
  stroke: var(--color-jv-surface-container-high);
}
</style>
